import {useMemo} from "react";

import {RequestState} from "@pg-mono/request-state";

import {useGetApplicationOfferDetailQuery, useGetApplicationPropertyDetailQuery, useGetApplicationVendorDetailQuery} from "../api/application_lead_data_api";
import {IAdditionalData, IApplicationReducerData} from "../types/IApplicationReducerData";

interface IUseLeadDataParams {
    vendorId: number;
    offerId?: number;
    propertyId?: number;
    skipFetch?: boolean;
}

function mapQueryToAdditionalData<T>(query: {data?: T; isLoading: boolean; isSuccess: boolean; isError: boolean; error?: unknown}): IAdditionalData<T> | null {
    if (query.isLoading) {
        return {request: RequestState.Waiting};
    }

    if (query.isSuccess && query.data) {
        return {request: RequestState.Success, detail: query.data};
    }

    if (query.isError) {
        return {request: RequestState.Error};
    }

    return null;
}

export function useLeadData(params: IUseLeadDataParams): IApplicationReducerData | null {
    const {vendorId, offerId, propertyId, skipFetch} = params;

    const vendorQuery = useGetApplicationVendorDetailQuery({vendorId}, {skip: skipFetch || !vendorId});
    const offerQuery = useGetApplicationOfferDetailQuery({offerId: offerId!}, {skip: skipFetch || !offerId});
    const propertyQuery = useGetApplicationPropertyDetailQuery({propertyId: propertyId!}, {skip: skipFetch || !propertyId});

    const isAnyLoading = vendorQuery.isLoading || offerQuery.isLoading || propertyQuery.isLoading;
    const isAllRequiredSuccess = vendorQuery.isSuccess && (!offerId || offerQuery.isSuccess) && (!propertyId || propertyQuery.isSuccess);
    const isAnyError = vendorQuery.isError || offerQuery.isError || propertyQuery.isError;

    return useMemo<IApplicationReducerData | null>(() => {
        if (isAnyLoading) {
            return null;
        }

        if (!isAllRequiredSuccess && !isAnyError) {
            return null;
        }

        const leadDataRequest = isAnyError ? RequestState.Error : isAllRequiredSuccess ? RequestState.Success : RequestState.Waiting;

        return {
            request: RequestState.None,
            application: null,
            error: null,
            vendor: mapQueryToAdditionalData(vendorQuery),
            offer: mapQueryToAdditionalData(offerQuery),
            property: mapQueryToAdditionalData(propertyQuery),
            leadDataRequest
        };
    }, [isAnyLoading, isAllRequiredSuccess, isAnyError, vendorQuery.data, offerQuery.data, propertyQuery.data]);
}
